import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useAttachSupplierInvoicePdf } from "~/hooks/purchasing";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

import type { SupplierInvoice } from "~/types/purchasing";

// Public document store (same bucket document-upload.ts uses) — the uploaded file becomes a
// permanent public URL, keeping the manual attach the same "just a URL" model as the BSale webhook.
const BUCKET = "sales-request-files";
const MAX_MB = 5;

interface AdjuntarPdfDialogProps {
  invoice: SupplierInvoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdjuntarPdfDialog({
  invoice,
  open,
  onOpenChange,
}: AdjuntarPdfDialogProps) {
  const attach = useAttachSupplierInvoicePdf();
  const [tab, setTab] = useState<"upload" | "url">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  // Reset when the dialog opens for a (different) factura.
  useEffect(() => {
    setTab("upload");
    setFile(null);
    setUrl("");
  }, [invoice?.id, open]);

  const busy = uploading || attach.isPending;
  const close = () => onOpenChange(false);

  const handleUpload = async () => {
    if (!invoice || !file) return;
    if (file.type !== "application/pdf") {
      toast({
        title: "Archivo inválido",
        description: "El documento debe ser un PDF.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: "Archivo muy grande",
        description: `El PDF supera el máximo de ${MAX_MB} MB.`,
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const path = `supplier-invoices/${invoice.id}/${Date.now()}_${safe}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: "application/pdf",
        });
      if (upErr) throw new Error(upErr.message);
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      if (!data?.publicUrl) throw new Error("No se pudo obtener la URL del archivo.");
      attach.mutate(
        { invoiceId: invoice.id, url: data.publicUrl },
        { onSuccess: close }
      );
    } catch (e) {
      toast({
        title: "Error al subir",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUrl = () => {
    if (!invoice) return;
    const clean = url.trim();
    if (!/^https?:\/\//i.test(clean)) {
      toast({
        title: "Enlace inválido",
        description: "Ingresa una URL que comience con http(s)://",
        variant: "destructive",
      });
      return;
    }
    attach.mutate({ invoiceId: invoice.id, url: clean }, { onSuccess: close });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjuntar factura</DialogTitle>
          <DialogDescription>
            {invoice
              ? `${invoice.razon_social ?? ""} · Folio ${invoice.folio}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "upload" | "url")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Subir archivo</TabsTrigger>
            <TabsTrigger value="url">Pegar enlace</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-2 pt-4">
            <Input
              type="file"
              accept="application/pdf"
              className="h-9"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">PDF, máximo {MAX_MB} MB.</p>
          </TabsContent>

          <TabsContent value="url" className="space-y-2 pt-4">
            <Input
              type="url"
              placeholder="https://…"
              className="h-9"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Enlace al DTE (dte.bsale.cl) o al PDF guardado (Drive, correo, etc.).
            </p>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancelar
          </Button>
          <Button
            onClick={tab === "upload" ? handleUpload : handleUrl}
            disabled={busy || (tab === "upload" ? !file : !url.trim())}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Adjuntar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
